/**
 * Insights — Overview (default tab)
 *
 * Honest version. Hero KPIs come from /insights-module/summary +
 * /insights-module/quality. Sections that have no backing endpoint
 * in this build (SLOs, AI weekly summary, Action Items, usage heatmap,
 * ROI metrics) are rendered as empty-state placeholders so we stop
 * showing hardcoded numbers.
 *
 * Backend gaps tracked separately — see MOCK-INVENTORY-2026-05-06.md.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Users, MessageSquare, ShieldCheck, ThumbsUp, AlertTriangle, DollarSign,
  Activity, Lightbulb, Sparkles, ShieldAlert, TrendingUp, ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams, rangeLabel,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import {
  useInsightsSummary, useInsightsQuality,
  useInsightsValue, useInsightsRisk,
} from '@/lib/use-insights';

export const Route = createFileRoute('/insights/')({
  component: InsightsOverview,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

function InsightsOverview() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — every hook below now picks up filter changes
  // via the params + queryKey wiring. Changing department / agent /
  // model in the FilterBar triggers refetches everywhere.
  const filterParams = filtersToParams(filters);
  // Pod-KK #111 — KPI/chart labels now reflect the selected range
  // instead of being hardcoded to "(30d)". Mirrors the wire-format
  // range so the UI never drifts from the data.
  const rl = rangeLabel(filters.range);
  const { data: summary, isLoading: summaryLoading } = useInsightsSummary(filterParams);
  const { data: quality, isLoading: qualityLoading } = useInsightsQuality(filterParams);
  // R3 P1 — value (ROI) + risk (Safety) wired into Overview cards.
  // Use the FilterBar range for these too rather than hard-coding 7d.
  const { data: value7d } = useInsightsValue(filterParams.range, {
    department: filterParams.department,
    agentId: filterParams.agentId,
    modelId: filterParams.modelId,
  });
  const { data: risk7d } = useInsightsRisk(filterParams.range, {
    department: filterParams.department,
    agentId: filterParams.agentId,
    modelId: filterParams.modelId,
  });

  const dau = summary?.uniqueUsers30d ?? 0;
  const queries = summary?.totalQueries ?? 0;
  const cost = summary
    ? Math.round(summary.totalCostDollars * 100) / 100
    : 0;
  const successRate = quality ? quality.successRate / 100 : 0;

  const hoursSaved7d = value7d?.hoursSaved ?? 0;
  const tasksAutomated7d = value7d?.tasksAutomated ?? 0;
  const incidents7d = risk7d?.totalIncidents ?? 0;
  const blocked7d = risk7d?.blockedActions ?? 0;

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — show an explicit banner when 'Custom range' was
          selected so the user knows the dashboard is actually showing 7d.
          Previously the dashboard silently substituted 7d. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* ── Hero KPI cards (real) ───────────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          <KpiCard
            label={`Active Users (${rl})`}
            icon={Users}
            value={summaryLoading ? '—' : dau.toLocaleString()}
            numericValue={dau}
            subtitle="Distinct users in query metrics"
            drillDownUrl="/insights/adoption"
          />
          <KpiCard
            label={`Queries (${rl})`}
            icon={MessageSquare}
            value={summaryLoading ? '—' : queries.toLocaleString()}
            numericValue={queries}
            subtitle={`${summary?.totalTokens?.toLocaleString() ?? '0'} tokens`}
            drillDownUrl="/insights/adoption"
          />
          <KpiCard
            label={`Success Rate (${rl})`}
            icon={ShieldCheck}
            value={qualityLoading ? '—' : `${(successRate * 100).toFixed(1)}%`}
            numericValue={successRate}
            subtitle={
              quality
                ? `${quality.failedQueries30d} failed of ${quality.totalQueries30d}`
                : '—'
            }
            statusDot={successRate >= 0.95 ? 'emerald' : successRate >= 0.85 ? 'amber' : 'red'}
            drillDownUrl="/insights/quality"
          />
          <KpiCard
            label={`Spend (${rl})`}
            icon={DollarSign}
            value={summaryLoading ? '—' : `$${cost.toLocaleString()}`}
            numericValue={cost}
            subtitle={`${summary?.uniqueAgents30d ?? 0} unique agents`}
            drillDownUrl="/insights/cost"
          />
        </div>

        {/* ── R3 P1 — Risk summary card wired to /risk ───────────────────── */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-1.5">
              <ShieldAlert className="h-4 w-4 text-muted-foreground" />
              Risk summary ({filterParams.range})
              <Link
                to="/insights/risk"
                className="ml-auto text-[10px] font-normal text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                View detail
                <ArrowRight className="h-3 w-3" />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {risk7d === undefined ? (
              <EmptySection
                message="Loading risk telemetry…"
                minHeight="min-h-20"
              />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Total incidents
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {incidents7d.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Open
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {(risk7d.openIncidents ?? 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Blocked actions
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {blocked7d.toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Failed audit
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {(risk7d.failedAuditEvents ?? 0).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard
            title="This week's changes"
            icon={Sparkles}
            className="lg:col-span-2"
          >
            <EmptySection
              message="AI weekly summary not yet available."
              hint="Requires an LLM-summarization job over the last 7 days of query/audit/feedback data."
            />
          </ChartCard>

          <ChartCard title="Action Items" icon={Lightbulb}>
            <EmptySection
              message="No action-items engine in this build."
              hint="A rules engine over query, cost, quality, and trust signals is required to populate this list."
            />
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ChartCard
            title="Usage by hour of day"
            icon={Activity}
            className="lg:col-span-2"
          >
            <EmptySection
              message="Usage heatmap not yet available."
              hint="Backend exposes daily aggregates only. Hour-of-day buckets require a dedicated /insights-module endpoint."
              minHeight="min-h-48"
            />
          </ChartCard>

          {/* R3 P1 — wired to /insights-module/value */}
          <ChartCard
            title={`Estimated value (${filterParams.range})`}
            icon={TrendingUp}
          >
            {value7d === undefined ? (
              <EmptySection
                message="Loading value metrics…"
                minHeight="min-h-48"
              />
            ) : (
              <div className="flex flex-col gap-2 px-2 py-3 min-h-48">
                <div>
                  <div className="text-3xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                    {hoursSaved7d.toLocaleString(undefined, {
                      maximumFractionDigits: 1,
                    })}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    hours saved over the last {filterParams.range}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
                  <div>
                    <div className="text-[10px] text-muted-foreground">
                      Queries handled
                    </div>
                    <div className="text-sm font-semibold tabular-nums">
                      {(value7d.queriesHandled ?? 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground">
                      Tasks automated
                    </div>
                    <div className="text-sm font-semibold tabular-nums">
                      {tasksAutomated7d.toLocaleString()}
                    </div>
                  </div>
                </div>
                <Link
                  to="/insights/value"
                  className="mt-auto text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  Open value detail
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </ChartCard>
        </div>

        {/* Suppress lint for unused icons in case of future expansion */}
        <div className="hidden">
          <ThumbsUp />
          <AlertTriangle />
        </div>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
